import React, { useEffect, useState } from 'react';

import { X } from 'lucide-react';

import {
	isStatementImage,
	sortStatementPhotos,
	STATEMENT_IMAGE_EXTENSIONS,
} from '../../utils/statementUploadFormats';
import {
	Button,
	FileUploadDropzone,
	Modal,
	ModalBody,
	ModalFooter,
} from '../common';

import type { UploadAction } from './DocumentCheckBanner';

export type AddPagesMode = UploadAction['mode'];

// Keep in sync with the /files/<id>/pages limits in statement_api.py.
const MAX_FILES = 10;
const MAX_TOTAL_BYTES = 45 * 1024 * 1024;
const PAGE_ACCEPT = ['pdf', ...STATEMENT_IMAGE_EXTENSIONS]
	.map((ext) => `.${ext}`)
	.join(',');
const PAGE_HINT =
	'PDF or photos (JPG, PNG, HEIC), up to 10 files and 45MB in total';

const COPY: Record<AddPagesMode, { body: string; submit: string }> = {
	append: {
		body: 'Add only the pages that are missing. To send the whole statement again, use Upload the complete statement.',
		submit: 'Add and reanalyze',
	},
	replace: {
		body: 'Upload the complete statement as a PDF or photos of every page. It replaces the file you sent, and we analyze it again.',
		submit: 'Replace and reanalyze',
	},
};

const isPageFile = (file: File): boolean =>
	file.type === 'application/pdf' ||
	file.name.toLowerCase().endsWith('.pdf') ||
	isStatementImage(file);

const fileKey = (file: File): string =>
	`${file.name}-${file.size}-${file.lastModified}`;

/** Files picked again (or dropped twice) are added once. */
export const mergePageFiles = (current: File[], picked: File[]): File[] => {
	const byKey = new Map(current.map((file) => [fileKey(file), file]));
	picked.forEach((file) => byKey.set(fileKey(file), file));
	return sortStatementPhotos([...byKey.values()]);
};

export const pageFilesError = (files: File[]): string | null => {
	if (!files.every(isPageFile))
		return 'Add PDF pages or photos (JPG, PNG or HEIC).';
	if (files.length > MAX_FILES) return `Add up to ${MAX_FILES} files at once.`;
	if (files.reduce((sum, file) => sum + file.size, 0) > MAX_TOTAL_BYTES)
		return 'The files must be 45MB or less in total.';
	return null;
};

interface AddPagesModalProps {
	request: UploadAction | null;
	/** From the API: this upload costs a report. */
	usesReport: boolean;
	/** From the API: a whole statement sent as pages replaces this one, and that costs a report. */
	wholeStatementUsesReport?: boolean;
	isSubmitting: boolean;
	onClose: () => void;
	onSubmit: (files: File[]) => void;
}

/** Pick the files for the banner's upload action (ROC-90). */
const AddPagesModal: React.FC<AddPagesModalProps> = ({
	request,
	usesReport,
	wholeStatementUsesReport = false,
	isSubmitting,
	onClose,
	onSubmit,
}) => {
	const [files, setFiles] = useState<File[]>([]);
	const [error, setError] = useState('');
	const [dragActive, setDragActive] = useState(false);
	useEffect(() => {
		setFiles([]);
		setError('');
	}, [request]);
	if (!request) return null;

	const addFiles = (picked: File[]) => {
		const next = mergePageFiles(files, picked);
		const problem = pageFilesError(next);
		setError(problem ?? '');
		if (!problem) setFiles(next);
	};
	const handleDrag = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(e.type === 'dragenter' || e.type === 'dragover');
	};
	const handleDrop = (e: React.DragEvent) => {
		handleDrag(e);
		if (!isSubmitting) addFiles(Array.from(e.dataTransfer.files ?? []));
	};
	const close = isSubmitting ? () => {} : onClose;
	const copy = COPY[request.mode];

	return (
		<Modal
			isOpen
			onClose={close}
			size="md"
			title={request.label}
			closeOnOverlayClick={!isSubmitting}
			showCloseButton={!isSubmitting}
		>
			<ModalBody>
				<p className="mb-4 text-sm text-gray-600">{copy.body}</p>
				<FileUploadDropzone
					accept={PAGE_ACCEPT}
					description={PAGE_HINT}
					onFileSelect={(file) => addFiles([file])}
					onFilesSelect={addFiles}
					selectedFile={null}
					dragActive={dragActive}
					onDragEnter={handleDrag}
					onDragLeave={handleDrag}
					onDragOver={handleDrag}
					onDrop={handleDrop}
					disabled={isSubmitting}
					error={error}
				/>
				{files.length > 0 && (
					<ol className="mt-3 list-decimal space-y-1 pl-5 text-sm text-gray-700">
						{files.map((file) => (
							<li key={fileKey(file)}>
								<span className="inline-flex w-full items-center justify-between gap-2">
									<span className="truncate" title={file.name}>
										{file.name}
									</span>
									<button
										type="button"
										className="rounded p-1 text-gray-400 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
										aria-label={`Remove ${file.name}`}
										disabled={isSubmitting}
										onClick={() => {
											setFiles((prev) => prev.filter((f) => f !== file));
											setError('');
										}}
									>
										<X className="h-4 w-4" aria-hidden="true" />
									</button>
								</span>
							</li>
						))}
					</ol>
				)}
				{usesReport ? (
					<p className="mt-3 text-sm font-medium text-gray-900">
						This uses 1 report.
					</p>
				) : (
					request.mode === 'append' &&
					wholeStatementUsesReport && (
						<p className="mt-3 text-sm text-gray-600">
							If the file is a whole statement, it replaces this one and uses 1
							report.
						</p>
					)
				)}
			</ModalBody>
			<ModalFooter>
				<Button variant="outline" onClick={close} disabled={isSubmitting}>
					Cancel
				</Button>
				<Button
					variant="primary"
					onClick={() => onSubmit(files)}
					disabled={files.length === 0}
					loading={isSubmitting}
				>
					{copy.submit}
				</Button>
			</ModalFooter>
		</Modal>
	);
};

export default AddPagesModal;
